import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { STATUSES, statusVar } from '../plan/arc';
import type { Arc, Status } from '../plan/arc';
import { needsEvidence } from '../plan/edits';
import { ArchivedGlyph, StatusGlyph } from './StatusGlyph';

/** Between the trigger and the panel, and from the viewport edge when the
 *  panel has to be pushed back inside it. */
const GAP = 6;

/**
 * Four rows and a number key each. Opens from a status glyph or a chip.
 *
 * `done` asks for its sentence inline, and accepts an empty one. Refusing to
 * close an arc until a box is filled makes the box the point; the evidence
 * convention is worth keeping by hand, not by a wall.
 *
 * Filing away rides here too, under a rule, because a second control for it
 * would be a second thing to find for a state that answers the same question
 * the reader opened this panel to answer — where does this arc stand. It is
 * kept below the rule and off the number keys because it is a DIFFERENT field:
 * `visibility:`, which supersedes the status without replacing it, so the arc
 * still says what state the work was left in.
 *
 * [anchor] is the rect of whatever was pressed. Without one the panel keeps
 * the position its stylesheet gives it, which is how the entity page's chip
 * places it — inside a holder that is already the right box. A board row has
 * no such holder: the panel is rendered as a far-away sibling of the row, so
 * an unanchored one landed in the corner of the screen, a long way from the
 * arc whose state it was about to change.
 */
export function StatusPicker({
  arc,
  anchor,
  onPick,
  onArchive,
  onClose,
}: {
  arc: Arc;
  anchor?: DOMRect | null;
  onPick: (status: Status, evidence?: string) => void;
  onArchive?: (archived: boolean) => void;
  onClose: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [evidence, setEvidence] = useState('');
  const box = useRef<HTMLDivElement>(null);

  /**
   * Before paint, so the panel is never seen in the wrong place. It opens
   * below the trigger and flips above it near the bottom of the window, which
   * is exactly where the rows in a long column are.
   *
   * Re-run when [asking] changes: answering `done` swaps the rows for the
   * evidence field and the panel changes height under a flip that was decided
   * for the old one.
   */
  useLayoutEffect(() => {
    const panel = box.current;
    if (!anchor || !panel) return;
    const { width, height } = panel.getBoundingClientRect();

    const below = anchor.bottom + GAP;
    const top = below + height > window.innerHeight - GAP ? anchor.top - height - GAP : below;
    const left = Math.min(anchor.left, window.innerWidth - width - GAP);

    panel.style.position = 'fixed';
    panel.style.top = `${Math.max(GAP, top)}px`;
    panel.style.left = `${Math.max(GAP, left)}px`;
    panel.style.right = 'auto';
  }, [anchor, asking]);

  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) onClose();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // Deferred so the click that opened it does not immediately close it.
    const id = setTimeout(() => document.addEventListener('mousedown', away));
    document.addEventListener('keydown', key);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const choose = (status: Status) => {
    if (status === 'done' && needsEvidence(arc)) {
      setAsking(true);
      return;
    }
    onPick(status);
  };

  return (
    <div className="picker" ref={box} role="menu">
      {!asking &&
        STATUSES.map((status, i) => (
          <button
            key={status}
            type="button"
            role="menuitem"
            className={`picker-row${arc.status === status ? ' current' : ''}`}
            style={
              arc.status === status
                ? { background: `color-mix(in srgb, var(${statusVar(status)}) 13%, transparent)` }
                : undefined
            }
            onClick={() => choose(status)}
          >
            <StatusGlyph status={status} />
            <span className="t-chipLabel">{status}</span>
            <kbd className="kbd">{i + 1}</kbd>
          </button>
        ))}

      {!asking && onArchive && (
        <>
          <span className="picker-rule" aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            className="picker-row picker-file"
            onClick={() => onArchive(!arc.archived)}
          >
            {arc.archived ? <StatusGlyph status={arc.status} /> : <ArchivedGlyph />}
            <span className="t-chipLabel">{arc.archived ? 'put back' : 'archive'}</span>
          </button>
        </>
      )}

      {asking && (
        <div className="picker-ask">
          <label className="t-eyebrowMono picker-ask-label" htmlFor="evidence">
            how do you know?
          </label>
          <input
            id="evidence"
            autoFocus
            className="t-chipLabel"
            value={evidence}
            placeholder="a commit, a check, a conversation…"
            onChange={(e) => setEvidence(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onPick('done', evidence.trim() || undefined);
            }}
          />
          <p className="t-metaMono picker-foot">↵ marks it done — with this, or with nothing</p>
        </div>
      )}
    </div>
  );
}
